import * as React from 'react';
import { useSelector } from 'react-redux';
import Flex from 'antd/lib/flex';
import Spin from 'antd/lib/spin';
import Popover from 'antd/lib/popover';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { compact, flatMap, isNil, keyBy, sortBy, uniq } from 'lodash';
import { RootState } from '@/reducers/reducers';
import {
    selectIsAppsDemoModeEnabled,
    selectMonitoringMetricsById,
    selectStabilityIntegrations,
    selectStepConfigsByType,
} from '@/selectors/selectors';
import { useGetRolloutGroupSummaryQuery } from '@/middlewares/defaultApi';
import { buildDemoRolloutGroupSummary } from '@/helpers/demoHelpers';
import {
    AggregatedMonitoringStatistic,
    DestinationMonitoringStatistic,
    DestinationAdoption,
    DestinationPhasedRelease,
    IntegrationType,
    MonitoringMetricHealthStatus,
    MonitoringStatistic,
    ReleaseStepType,
    StabilityMetricType,
    VersionAdoption,
} from '@/types';
import { Destination } from '@/types/destinations';
import { displayMonitoringStatisticUnitLabel, GroupedMonitoringStatistic } from '@/typescriptHelpers';
import {
    displayMonitoringStatisticFormattedDelta,
    displayMonitoringStatisticFormattedValue,
    getIntegrationTypeForAppStoreType,
    massageMonitoringData,
} from '@/helpers';
import { colorForMonitoringMetricHealthStatus, displayStringForDestination } from '@/helpers/displayHelpers';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { antGrey, runwayColors, tagStyle } from '@/components/styles/globalStyles';
import Tag from 'antd/lib/tag';
import { AppstoreOutlined, ThunderboltFilled } from '@ant-design/icons';
import { PhasedReleaseChartBase } from '@/components/appHome/widgets/LiveReleaseChartWidget';
import IntegrationIcon from '@/components/icons/IntegrationIcon';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import {
    MonitoringMetricCard,
    monitoringMetricCardsForStatistic,
    relevantGroupsForMonitoringMetricCard,
} from '@/components/monitoring/MonitoringMetricCard';
import StabilityIssuesContainer from '@/components/monitoring/StabilityIssuesContainer';
import { CollapsibleSection } from '@/components/monitoring/CollapsibleSection';
import RatingsReviews from '@/components/RatingsReviews';

const METRIC_CARDS_GRID_STYLE: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(232px, 1fr))',
    gap: 12,
};

interface RolloutGroupSummaryHeaderProps {
    orgId: string;
    appId: string;
    workflowId: string;
    workflowRunId: string;
    stepRunIds: string[];
}

const seriesKeyForStatistic = (
    stat: Pick<MonitoringStatistic, 'type' | 'integrationInstallationId'> & Partial<MonitoringStatistic>,
) => JSON.stringify([stat.type, stat.integrationInstallationId, stat.eventName, stat.paramName]);

const unmergedStabilityTypes: Record<string, StabilityMetricType> = {
    [StabilityMetricType.MergedSessionStability]: StabilityMetricType.SessionStability,
    [StabilityMetricType.MergedUserStability]: StabilityMetricType.UserStability,
};

const unmergedSeriesKey = (seriesKey: string): string => {
    const [type, ...rest] = JSON.parse(seriesKey);
    return JSON.stringify([unmergedStabilityTypes[type] ?? type, ...rest]);
};

const displayValueForStatistic = (stat: MonitoringStatistic): string => {
    const unit = displayMonitoringStatisticUnitLabel(stat);
    const value = displayMonitoringStatisticFormattedValue(stat);
    const valueWithUnit = unit ? `${value} ${unit}` : value;
    const delta = displayMonitoringStatisticFormattedDelta(stat);
    if (!delta || stat.currentValue === undefined || !stat.previousValue) {
        return valueWithUnit;
    }
    return `${valueWithUnit} ${stat.currentValue >= stat.previousValue ? '↑' : '↓'} ${delta}`;
};

const destinationStatisticsForCard = (
    card: GroupedMonitoringStatistic,
    aggregatedStatistics: AggregatedMonitoringStatistic[],
    destinationsByStepConfigId: Record<string, Destination>,
): DestinationMonitoringStatistic[] => {
    const destinationStepConfigIds = Object.keys(destinationsByStepConfigId);
    const statistics = flatMap(relevantGroupsForMonitoringMetricCard(card.groups), (group) => {
        const aggregated = aggregatedStatistics.find((stat) =>
            group.monitoringMetricId ?
                stat.monitoringMetricId === group.monitoringMetricId
            :   seriesKeyForStatistic(stat) === unmergedSeriesKey(group.seriesKey),
        );
        return aggregated?.destinationStatistics ?? [];
    }).filter((stat) => !!destinationsByStepConfigId[stat.destinationStepConfigId]);
    return sortBy(statistics, (stat) => destinationStepConfigIds.indexOf(stat.destinationStepConfigId));
};

const DestinationValuesPopoverContent: React.FC<{
    card: GroupedMonitoringStatistic;
    destinationStatistics: DestinationMonitoringStatistic[];
    destinationsByStepConfigId: Record<string, Destination>;
}> = ({ card, destinationStatistics, destinationsByStepConfigId }) => {
    const label = card.customDisplayName || card.title;

    return (
        <Flex vertical gap={8}>
            {destinationStatistics.map((stat) => {
                const destination = destinationsByStepConfigId[stat.destinationStepConfigId];
                if (!destination) {
                    return null;
                }
                return (
                    <Flex
                        key={`${stat.destinationStepConfigId}-${stat.type}`}
                        align="center"
                        gap={8}
                        style={{
                            position: 'relative',
                            height: 40,
                            padding: '0 12px 0 18px',
                            backgroundColor: antGrey.grey2,
                            border: `1px solid ${antGrey.grey4}`,
                            borderRadius: 6,
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                width: 5,
                                backgroundColor: colorForMonitoringMetricHealthStatus(stat.monitoringMetricStatus),
                            }}
                        />
                        <DestinationCounter destinations={[destination]} size={16} />
                        <IntegrationIcon integrationId={stat.integrationId} size={16} />
                        <Text type="secondary" ellipsis={{ tooltip: label }} style={{ flex: 1, fontSize: 12 }}>
                            {label}
                        </Text>
                        <Text style={{ fontSize: 13 }}>{displayValueForStatistic(stat as MonitoringStatistic)}</Text>
                    </Flex>
                );
            })}
        </Flex>
    );
};

const phasedReleaseStatusLabel = (releases: DestinationPhasedRelease[]): string => {
    const isFinished = (release: DestinationPhasedRelease) => release.isCompleted || release.isSuperseded;
    if (releases.some((release) => !isFinished(release) && !release.isPaused)) {
        return 'Active';
    }
    if (releases.some((release) => !isFinished(release))) {
        return 'Paused';
    }
    return releases.every((release) => release.isSuperseded) ? 'Superseded' : 'Completed';
};

const SummaryRowsCard: React.FC<{
    title: string;
    tag?: React.ReactNode;
    renderIcons: ((size: number) => React.ReactNode)[];
    footer?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, tag, renderIcons, footer, children }) => (
    <div
        style={{
            padding: '16px 20px 12px 22px',
            backgroundColor: runwayColors.white,
            border: `1px solid ${antGrey.grey5}`,
            borderRadius: 8,
            flex: 1,
        }}
    >
        <Flex vertical gap={10}>
            <Flex align="center" justify="space-between" style={{ height: 24 }}>
                <Flex align="center" gap={12}>
                    <Text
                        style={{
                            fontSize: 14,
                            lineHeight: '22px',
                            fontWeight: 600,
                            letterSpacing: 0,
                            color: antGrey.grey8,
                        }}
                    >
                        {title}
                    </Text>
                    {tag}
                </Flex>
                {renderIcons.length === 1 && renderIcons[0](16)}
                {renderIcons.length > 1 && (
                    <Popover
                        overlayInnerStyle={{ padding: '11px 12px' }}
                        content={
                            <Flex align="center" gap={24}>
                                {renderIcons.map((renderIcon, i) => (
                                    <React.Fragment key={i}>{renderIcon(16)}</React.Fragment>
                                ))}
                            </Flex>
                        }
                    >
                        <AppstoreOutlined style={{ color: antGrey.grey7, fontSize: 16 }} />
                    </Popover>
                )}
            </Flex>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '24px minmax(0, 1fr) 62px',
                    alignItems: 'center',
                    columnGap: 8,
                    rowGap: 4,
                }}
            >
                {children}
            </div>
            {footer}
        </Flex>
    </div>
);

const ROW_VALUE_TEXT_STYLE: React.CSSProperties = {
    fontSize: 12,
    lineHeight: '20px',
    fontWeight: 400,
    letterSpacing: '0.1em',
    color: antGrey.grey7,
};

const DestinationValueRow: React.FC<{
    destination: Destination;
    bar: React.ReactNode;
    trailingIcon?: React.ReactNode;
    value: string;
}> = ({ destination, bar, trailingIcon, value }) => (
    <>
        <Flex align="center" style={{ height: 22 }}>
            <DestinationCounter destinations={[destination]} size={20} />
        </Flex>
        <div>{bar}</div>
        <Flex align="center" justify="flex-end" gap={8}>
            {trailingIcon}
            <Text style={{ ...ROW_VALUE_TEXT_STYLE, whiteSpace: 'nowrap' }}>{value}</Text>
        </Flex>
    </>
);

const PhasedReleaseDestinationsCard: React.FC<{
    releases: { destination: Destination; release: DestinationPhasedRelease }[];
}> = ({ releases }) => {
    const storeIcons = compact(
        uniq(releases.map(({ destination }) => getIntegrationTypeForAppStoreType(destination.appStoreType))),
    ).map((integrationType) => iconAndAltForIntegrationType(integrationType));

    return (
        <SummaryRowsCard
            title="Phased release"
            tag={
                <Tag color={runwayColors.primary} style={{ ...tagStyle, margin: 0 }}>
                    {phasedReleaseStatusLabel(releases.map(({ release }) => release)).toUpperCase()}
                </Tag>
            }
            renderIcons={storeIcons.map(([icon, alt]) => (size: number) => (
                <img src={icon} alt={alt} width={size} height={size} />
            ))}
        >
            {releases.map(({ destination, release }) => (
                <DestinationValueRow
                    key={destination.stepConfigId}
                    destination={destination}
                    bar={
                        <PhasedReleaseChartBase
                            percentage={release.phasedReleasePercent}
                            dayNumber={release.dayNumber}
                            daysToPercent={release.daysToPercentSchedule}
                            isAutomatedPhasedRollout={release.isAutomatedPhasedRollout}
                            wasHalted={release.isPaused}
                            wasAccelerated={release.isAccelerated}
                            segmentBorderRadius={2}
                        />
                    }
                    trailingIcon={
                        release.isAutomatedPhasedRollout && (
                            <ThunderboltFilled style={{ color: runwayColors.primary }} />
                        )
                    }
                    value={`${release.phasedReleasePercent}%`}
                />
            ))}
        </SummaryRowsCard>
    );
};

const VersionAdoptionBar: React.FC<{ destination: Destination; versions: VersionAdoption[] }> = ({
    destination,
    versions,
}) => {
    const destinationName = displayStringForDestination(
        destination.platform,
        destination.appStoreType,
        destination.name,
        destination.appName,
    );
    const unknownAdoption = Math.max(100 - versions.reduce((sum, version) => sum + version.adoptionRate, 0), 0);

    return (
        <Flex align="center" gap={4} style={{ height: 8 }}>
            {versions.map((version) => (
                <Tooltip
                    key={version.version}
                    title={`${destinationName}: ${version.version} — ${Number(version.adoptionRate.toFixed(1))}%`}
                >
                    <div
                        style={{
                            flex: version.adoptionRate,
                            minWidth: 4,
                            height: 8,
                            borderRadius: 2,
                            backgroundColor: version.isCurrent ? runwayColors.primary : antGrey.grey5,
                        }}
                    />
                </Tooltip>
            ))}
            {unknownAdoption > 0 && (
                <div style={{ flex: unknownAdoption, height: 8, borderRadius: 2, backgroundColor: antGrey.grey4 }} />
            )}
        </Flex>
    );
};

interface AdoptionRateSection {
    installationId: string;
    integrationType?: IntegrationType;
    destinationAdoptions: { destination: Destination; adoption: DestinationAdoption }[];
}

const AdoptionRateDestinationsCard: React.FC<{ sections: AdoptionRateSection[] }> = ({ sections }) => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const section = sections[Math.min(selectedIndex, sections.length - 1)];
    const integrationType = section?.integrationType;

    return (
        <SummaryRowsCard
            title="Adoption rate"
            renderIcons={
                integrationType ?
                    [(size: number) => <IntegrationIcon integrationId={integrationType} size={size} />]
                :   []
            }
            footer={
                sections.length > 1 && (
                    <Flex justify="center" gap={8}>
                        {sections.map((candidate, i) => (
                            <div
                                key={candidate.installationId}
                                role="button"
                                aria-label={`Show adoption for integration ${i + 1}`}
                                onClick={() => setSelectedIndex(i)}
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    backgroundColor: candidate === section ? antGrey.grey7 : antGrey.grey4,
                                }}
                            />
                        ))}
                    </Flex>
                )
            }
        >
            {section && section.destinationAdoptions.length > 0 ?
                section.destinationAdoptions.map(({ destination, adoption }) => (
                    <DestinationValueRow
                        key={destination.stepConfigId}
                        destination={destination}
                        bar={<VersionAdoptionBar destination={destination} versions={adoption.versions} />}
                        value={`${Number(adoption.adoptionRate.toFixed(1))}%`}
                    />
                ))
            :   <Text style={{ ...ROW_VALUE_TEXT_STYLE, letterSpacing: 0, color: antGrey.grey6, gridColumn: '1 / -1' }}>
                    No adoption rates known.
                </Text>
            }
        </SummaryRowsCard>
    );
};

export const RolloutGroupSummaryHeader: React.FC<RolloutGroupSummaryHeaderProps> = ({
    orgId,
    appId,
    workflowId,
    workflowRunId,
    stepRunIds,
}) => {
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const demoStepRuns = useSelector((state: RootState) =>
        isViewingAppsInDemoMode ? state.ui.uiConfig?.demoData?.stepRunsDemoData : undefined,
    );
    const demoSummary = React.useMemo(
        () =>
            demoStepRuns ?
                buildDemoRolloutGroupSummary(compact(stepRunIds.map((stepRunId) => demoStepRuns[stepRunId])))
            :   undefined,
        [demoStepRuns, stepRunIds],
    );
    const { data: fetchedSummary, isLoading } = useGetRolloutGroupSummaryQuery(
        { orgId, workflowId, workflowRunId, stepRunIds },
        { skip: stepRunIds.length === 0 || isViewingAppsInDemoMode },
    );
    const summary = demoSummary ?? fetchedSummary;

    const metricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, appId));
    const stabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, appId));
    const rolloutStepConfigs = useSelector((state: RootState) =>
        selectStepConfigsByType(state, appId, ReleaseStepType.Rollout),
    );
    const rolloutInstallationIds = React.useMemo(
        () =>
            uniq(
                flatMap(Object.values(rolloutStepConfigs), (stepConfig) => stepConfig.integrationInstallationIds ?? []),
            ),
        [rolloutStepConfigs],
    );

    const destinations = summary?.destinations ?? [];
    const destinationsByStepConfigId = React.useMemo(() => keyBy(destinations, 'stepConfigId'), [destinations]);

    const aggregatedCards = React.useMemo(() => {
        if (!summary) {
            return [];
        }
        const grouped = massageMonitoringData({
            monitoringStatistics: summary.aggregatedMonitoringStatistics as MonitoringStatistic[],
            monitoringAutomations: summary.monitoringAutomations ?? [],
            metricsById,
            hasPartialData: true,
            stepInstallationIds: rolloutInstallationIds,
        });
        return flatMap(grouped, monitoringMetricCardsForStatistic).map((card) => {
            const displayedGroups = relevantGroupsForMonitoringMetricCard(card.groups);
            const groupsWithValue = displayedGroups.filter((group) => !isNil(group.currentValue));
            return { ...card, groups: groupsWithValue.length > 0 ? groupsWithValue : displayedGroups.slice(0, 1) };
        });
    }, [summary, metricsById, rolloutInstallationIds]);

    const unhealthyCards = React.useMemo(() => {
        if (!summary) {
            return [];
        }
        return flatMap(summary.unhealthyMonitoringStatistics, (stat) => {
            const grouped = massageMonitoringData({
                monitoringStatistics: [stat as MonitoringStatistic],
                monitoringAutomations: summary.monitoringAutomations ?? [],
                metricsById,
                hasPartialData: true,
                stepInstallationIds: rolloutInstallationIds,
                shouldGroupCrashFreeStats: false,
            });
            return flatMap(grouped, monitoringMetricCardsForStatistic)
                .filter((card) => card.groups.some((group) => group.status === MonitoringMetricHealthStatus.Unhealthy))
                .map((card) => ({ card, destinationStepConfigId: stat.destinationStepConfigId }));
        });
    }, [summary, metricsById, rolloutInstallationIds]);

    if (isLoading) {
        return (
            <Flex justify="center" style={{ padding: 24 }}>
                <Spin />
            </Flex>
        );
    }

    if (!summary || destinations.length === 0) {
        return null;
    }

    const adoptionSectionsWithData: AdoptionRateSection[] = Object.entries(summary.adoptionsByInstallationId ?? {}).map(
        ([installationId, adoptions]) => ({
            installationId,
            integrationType:
                stabilityIntegrations.find((integration) => integration.installationId === installationId)?.id ??
                (installationId.split(':')[0] as IntegrationType),
            destinationAdoptions: adoptions
                .filter((adoption) => !!destinationsByStepConfigId[adoption.destinationStepConfigId])
                .map((adoption) => ({
                    destination: destinationsByStepConfigId[adoption.destinationStepConfigId],
                    adoption,
                })),
        }),
    );
    const adoptionSections: AdoptionRateSection[] =
        adoptionSectionsWithData.length > 0 ?
            adoptionSectionsWithData
        :   [
                {
                    installationId: 'none',
                    integrationType: stabilityIntegrations.find((integration) =>
                        rolloutInstallationIds.includes(integration.installationId),
                    )?.id,
                    destinationAdoptions: [],
                },
            ];

    const phasedReleasesByDestinationStepId = summary.phasedReleasesByDestinationStepId ?? {};
    const destinationPhasedReleases = flatMap(destinations, (destination) => {
        const release = phasedReleasesByDestinationStepId[destination.stepConfigId];
        return release ? [{ destination, release }] : [];
    });
    const stabilityInfos = Object.entries(summary.stabilityInfosByInstallationId ?? {});
    const haveReviews = summary.reviewData.recentReviews.length > 0 || summary.reviewData.issues.length > 0;

    return (
        <Flex vertical gap={12} style={{ padding: '0 36px 24px' }}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: destinationPhasedReleases.length > 0 ? '1fr 1fr' : '1fr',
                    gap: 12,
                }}
            >
                <AdoptionRateDestinationsCard sections={adoptionSections} />
                {destinationPhasedReleases.length > 0 && (
                    <PhasedReleaseDestinationsCard releases={destinationPhasedReleases} />
                )}
            </div>

            {(aggregatedCards.length > 0 || unhealthyCards.length > 0) && (
                <div style={METRIC_CARDS_GRID_STYLE}>
                    {aggregatedCards.map((card) => {
                        const metricCard = (
                            <MonitoringMetricCard
                                {...card}
                                leadingIcon={<DestinationCounter destinations={destinations} size={18} />}
                            />
                        );
                        const destinationStatistics = destinationStatisticsForCard(
                            card,
                            summary.aggregatedMonitoringStatistics,
                            destinationsByStepConfigId,
                        ).filter((stat) => stat.currentValue !== undefined && stat.currentValue !== null);
                        if (destinationStatistics.length === 0) {
                            return <div key={`aggregated-${card.key}`}>{metricCard}</div>;
                        }
                        return (
                            <Popover
                                key={`aggregated-${card.key}`}
                                overlayStyle={{ width: 248 }}
                                content={
                                    <DestinationValuesPopoverContent
                                        card={card}
                                        destinationStatistics={destinationStatistics}
                                        destinationsByStepConfigId={destinationsByStepConfigId}
                                    />
                                }
                            >
                                <div>{metricCard}</div>
                            </Popover>
                        );
                    })}
                    {unhealthyCards.map(({ card, destinationStepConfigId }) => {
                        const destination = destinationsByStepConfigId[destinationStepConfigId];
                        return (
                            <MonitoringMetricCard
                                key={`unhealthy-${destinationStepConfigId}-${card.key}`}
                                {...card}
                                leadingIcon={
                                    destination && <DestinationCounter destinations={[destination]} size={18} />
                                }
                            />
                        );
                    })}
                </div>
            )}

            {stabilityInfos.map(([installationId, stabilityInfo]) => (
                <StabilityIssuesContainer
                    key={installationId}
                    appId={appId}
                    stabilityInfo={stabilityInfo}
                    shouldHaveWhiteBackground
                    destinationsByStepConfigId={destinationsByStepConfigId}
                />
            ))}

            {haveReviews && (
                <CollapsibleSection title="Reviews" shouldHaveWhiteBackground>
                    <RatingsReviews
                        reviewData={summary.reviewData}
                        orgId={orgId}
                        appId={appId}
                        destinationsByStepConfigId={destinationsByStepConfigId}
                    />
                </CollapsibleSection>
            )}
        </Flex>
    );
};
